import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useOverlayHistory } from '../useOverlayHistory'

// Maximizable wraps any panel with a ⛶ control (B in the simplified-flow
// plan): maximized, the panel takes the whole viewport chrome-free — the
// "use this panel independently, full screen" affordance. Esc or ⛶ exits.
// One panel maximizes at a time by construction (each instance owns its own
// overlay; opening one leaves others untouched behind it).
export function Maximizable({ title, children }: { title: string; children: ReactNode }) {
  const [max, setMax] = useState(false)
  // Maximized is a fullscreen view too, so Back gets it out — same contract as
  // the artifact frame rather than a second set of rules to learn.
  const restore = useCallback(() => setMax(false), [])
  const dismiss = useOverlayHistory(max, restore)

  useEffect(() => {
    document.body.classList.toggle('panel-maximized', max)
    if (!max) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.classList.remove('panel-maximized')
    }
  }, [max, dismiss])

  return (
    <div className={max ? 'maximizable maxed' : 'maximizable'}>
      <button
        className="max-btn"
        title={max ? 'Restore (Esc)' : `Maximize ${title}`}
        aria-pressed={max}
        onClick={() => (max ? dismiss() : setMax(true))}
      >
        {max ? '🗗' : '⛶'}
      </button>
      {max ? (
        <>
          <button className="artifact-back max-back" onClick={dismiss} title="Back (Esc)">
            <span aria-hidden>←</span> Back
          </button>
          <div className="max-title">{title}</div>
        </>
      ) : null}
      {children}
    </div>
  )
}
